import { serialize } from 'object-to-formdata';
import { IUserUpdateInputModel } from 'inputModels';
import { useEffect, useRef, useState } from 'react';
import {
  IRefRegisterPersonalData,
  IRefRegisterProfileData,
  IRefRegisterServiceData,
  IRefRegisterTypeProfile,
  IRefRegisterUrlData,
  IRefRegisterTypeServiceData,
} from 'entities';
import { IAddressViewModel } from 'viewModels';
import { NavHeader, MenuOptionLeftUser, Button } from '../../components';
import { RegisterPersonalData } from '../../components/RegisterPersonalData';
import { RegisterProfile } from '../../components/RegisterProfile';
import { RegisterService } from '../../components/RegisterService';
import { RegisterTypeProfile } from '../../components/RegisterTypeProfile';
import { RegisterUrl } from '../../components/RegisterUrl';
import { useApp, useAuth, useRegisterForm } from '../../context';
import { Container, Content } from './styles';
import { UserController } from '../../controller/UserController';
import { useAlert } from '../../hooks/useAlert';
import { useSpecialties } from '../../context/specialties';
import { useCorrespondences } from '../../context/correspondences';
import { RegisterTypeServices } from '../../components/RegisterTypeServices';
import { TutorialType } from '../../types/enums';
import { InviteFriend } from '../../components/InviteFriend';
import JoyrideTour from '../../components/JoyrideTour';
import TitlePage from '../../components/TitlePage';
import { prepareExternalContactsForSubmit } from '../../utils/validateExternalContact';

export function NaturalPersonAccount() {
  const { user } = useAuth();
  const alert = useAlert();
  const app = useApp();
  const [isSending, setIsSending] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const [hasPageForSearch, setHasPageForSearch] = useState<boolean>();
  const {
    hasNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialty,
    setSpecialtiesSelectedByUser,
  } = useSpecialties();
  const {
    isLoading,
    setFormSubmited,
    setFirstName,
    setLastName,
    setIsVerified,
    setInscriptionNumber,
    setTypeProfile,
    setTypeValidation,
    setVerificationPictures,
    setAddress,
    setIsServiceProvider,
    setPresentationLetter,
    setPictureProfile,
    setExternalContacts,
    setLinkFacebook,
    setLinkInstagram,
    setLinkLinkedIn,
    setLinkSite,
    setLinkTwitter,
    setUrl,
    setActiveForm,
    setDocument,
  } = useRegisterForm();
  const { setCorrespondencesSelectedByUser } = useCorrespondences();
  const personalDataRef = useRef<IRefRegisterPersonalData>(null);
  const profileTypeDataRef = useRef<IRefRegisterTypeProfile>(null);
  const serviceDataRef = useRef<IRefRegisterServiceData>(null);
  const serviceTypeDataRef = useRef<IRefRegisterTypeServiceData>(null);
  const profileDataRef = useRef<IRefRegisterProfileData>(null);
  const urlDataRef = useRef<IRefRegisterUrlData>(null);

  const steps = [
    {
      target: '#start-item',
      content:
        'A página de Perfil é onde você pode atualizar suas informações pessoais, de endereço, de serviços, entre outros.',
      disableBeacon: true,
    },
    {
      target: '#document-input',
      content:
        'Digite seu documento de identificação, caso seja pessoa física, digite seu CPF, caso seja pessoa jurídica, digite seu CNPJ.',
    },
    {
      target: '#name-input',
      content:
        'Após inserir seu documento, seu nome deve aparecer automaticamente, caso não apareça, digite seu nome completo.',
    },
    {
      target: '#profile-type-dropdown',
      content: 'Selecione o tipo de perfil',
    },
    {
      target: '#validation-type',
      content: 'Selecione um tipo de validação e adicione as imagens de verificação.',
    },
    {
      target: '#expand-register-service',
      content:
        'Você pode clicar para expandir esta aba e selecionar se deseja prestar ou não serviços, se for prestar serviços, selecione suas comarcas de atuação.',
    },
    {
      target: '#select-specialties',
      content: 'Selecione as suas especialidades, você pode selecionar mais de uma especialidade.',
    },
    {
      target: '#select-correspondences',
      content:
        'Selecione as correspondências que você deseja prestar serviços, você pode selecionar mais de uma correspondência.',
    },
    {
      target: '#contacts',
      content:
        'Aqui você pode adicionar contatos externos, como telefone, email, whatsapp, entre outros. e configurar suas redes sociais',
    },
    {
      target: '#profile-url',
      content: 'Aqui você pode adicionar mudar a URL de referencia para o seu perfil.',
    },
    {
      target: '#invite-friends',
      content: 'Aqui você pode convidar outras pessoas para a plataforma.',
    },
    {
      target: '#save-button',
      content: 'Clique aqui para salvar as alterações.',
    },
  ];

  const handleUpdateProfile = async () => {
    setFormSubmited(true);
    const personalData = await personalDataRef.current?.handleUpdatePersonalData();
    const profileTypeData = profileTypeDataRef.current?.handleUpdateTypeProfile();
    const serviceData = serviceDataRef.current?.handleUpdateServiceData();
    const profileData = await profileDataRef.current?.handleUpdateProfileData();
    const urlData = await urlDataRef.current?.handleUpdateUrlData();
    const serviceTypeData = serviceTypeDataRef.current?.handleUpdateTypeServiceData();
    if (profileTypeData?.hasError) {
      profileTypeDataRef.current?.handleFocusOnError();
      setActiveForm('registerTypeProfile');
      return;
    }
    // if (profileTypeData?.typeProfile === UserProfileType.LAWYER && !profileTypeData?.isVerified) {
    //   setActiveForm('registerTypeProfile');
    //   alert.show({
    //     message: 'Não é possível finalizar o cadastro sem a verificação do perfil!',
    //     type: 'error',
    //   });
    //   return;
    // }
    if (
      !personalData ||
      !profileTypeData ||
      !serviceData ||
      !profileData ||
      !urlData ||
      !serviceTypeData
    ) {
      alert.show({
        message: 'Falha ao tentar buscar os dados do formulário, tente novamente!',
        type: 'error',
      });
      return;
    }
    const hasError =
      personalData.hasError ||
      serviceData.hasError ||
      urlData.hasError ||
      serviceTypeData.hasError ||
      profileTypeData.hasError;
    if (hasError) {
      if (personalData.hasError) {
        personalDataRef.current?.handleFocusOnError();
        return;
      }
      if (profileTypeData.hasError) {
        profileTypeDataRef.current?.handleFocusOnError();
        setActiveForm('registerTypeProfile');
        return;
      }
      if (serviceData.hasError) {
        serviceDataRef.current?.handleFocusOnError();
        setActiveForm('registerService');
        return;
      }
      if (serviceTypeData.hasError) {
        serviceTypeDataRef.current?.handleFocusOnError();
        setActiveForm('registerTypeService');
        return;
      }
      return;
    }
    const update: IUserUpdateInputModel = {
      firstName: personalData.firstName,
      lastName: personalData.lastName,
      document: personalData.document,
      isVerified: profileTypeData.isVerified,
      inscriptionNumber: profileTypeData.inscriptionNumber,
      ufInscriptionNumber: profileTypeData?.ufInscriptionNumber,
      profileType: profileTypeData.typeProfile,
      validationType: profileTypeData.typeValidation,
      address: serviceData.address,
      isServiceProvider: serviceData.isProviderService,
      specialties: serviceTypeData.specialtiesSelected.map((specialty) => specialty.id),
      correspondences: serviceTypeData.correspondencesSelected.map(
        (correspondence) => correspondence.id,
      ),

      profilePictureId: profileData.pictureProfile?.id,
      externalContacts: prepareExternalContactsForSubmit(profileData.externalContacts),
      presentation: profileData.presentationLetter,
      linkFacebook: profileData.linkFacebook,
      linkInstagram: profileData.linkInstagram,
      linkLinkedIn: profileData.linkLinkedIn,
      linkSite: profileData.linkSite,
      linkTwitter: profileData.linkTwitter,
      url: urlData.url,
    };
    const formData = serialize(update, { indices: true });
    if (profileData.pictureProfile?.file)
      formData.append('profilePicture', profileData.pictureProfile?.file);

    if (
      profileTypeData.verificationPictures[0]?.file ||
      profileTypeData.verificationPictures[1]?.file
    ) {
      if (profileTypeData.verificationPictures[0]?.file)
        formData.append('verificationPictureFront', profileTypeData.verificationPictures[0].file);
      if (profileTypeData.verificationPictures[1]?.file)
        formData.append('verificationPictureBack', profileTypeData.verificationPictures[1].file);
    }

    setIsSending(true);
    UserController.update(formData)
      .then(() => {
        setFormSubmited(false);
      })
      .then(() => {
        alert.show({ message: 'Mudanças aplicadas com sucesso!', type: 'success' });
        setDocument(update.document ?? '');
        setFirstName(update.firstName ?? '');
        setLastName(update.lastName ?? '');
        setIsVerified(!!update.isVerified);
        setInscriptionNumber(update.inscriptionNumber ?? '');
        setTypeProfile(update.profileType);
        setTypeValidation(update.validationType);
        setVerificationPictures(profileTypeData.verificationPictures);
        setAddress(update.address ?? ({} as IAddressViewModel));
        setIsServiceProvider(!!update.isServiceProvider);
        setSpecialtiesSelectedByUser(serviceTypeData.specialtiesSelected);
        setCorrespondencesSelectedByUser(serviceTypeData.correspondencesSelected);
        setPresentationLetter(update.presentation ?? '');
        setPictureProfile(profileData.pictureProfile);
        setExternalContacts(profileData.externalContacts);
        setLinkFacebook(update.linkFacebook ?? '');
        setLinkInstagram(update.linkInstagram ?? '');
        setLinkLinkedIn(update.linkLinkedIn ?? '');
        setLinkSite(update.linkSite ?? '');
        setLinkTwitter(update.linkTwitter ?? '');
        setUrl(update.url ?? '');
        setActiveForm('');
      })
      .catch(() => {
        alert.show({
          message: 'Falha ao tentar atualizar o perfil, tente novamente!',
          type: 'error',
        });
      })
      .finally(() => setIsSending(false));
  };

  useEffect(() => {
    app.setLoading(isLoading || isSending);
  }, [app, isLoading, isSending]);

  useEffect(() => {
    setHasPageForSearch(
      hasNextPageSpecialtiesSelectedByUser ||
        isFetchingNextPageSpecialtiesSelectedByUser ||
        isFetchingNextPageSpecialty,
    );
  }, [
    hasNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialtiesSelectedByUser,
    isFetchingNextPageSpecialty,
  ]);

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.NATURAL_PERSON_ACCOUNT)) {
      profileTypeDataRef.current?.handleTourDefaultValues();
      serviceTypeDataRef.current?.handleTourDefaultValues();
      setRunningTutorial(true);
    }
  }, [user?.tutorialProgress]);

  return (
    <>
      <JoyrideTour
        steps={steps}
        type={TutorialType.NATURAL_PERSON_ACCOUNT}
        run={runningTutorial}
        onCallbackFinished={() => setRunningTutorial(false)}
      />
      <NavHeader typeHeader="user" />
      <Container>
        <MenuOptionLeftUser />
        <Content>
          <div className="page-title">
            <TitlePage
              title="Perfil"
              onRequestOpen={() => {
                profileTypeDataRef.current?.handleTourDefaultValues();
                serviceTypeDataRef.current?.handleTourDefaultValues();
                setRunningTutorial(true);
              }}
              id="start-item"
            />
          </div>

          <RegisterPersonalData ref={personalDataRef} />
          <RegisterTypeProfile ref={profileTypeDataRef} />
          <RegisterService ref={serviceDataRef} />
          <RegisterTypeServices ref={serviceTypeDataRef} />
          <RegisterProfile ref={profileDataRef} />
          <RegisterUrl ref={urlDataRef} />
          <InviteFriend />
          <Button
            text="Salvar"
            id="save-button"
            dimensions={{ width: 150, height: 30 }}
            onClick={handleUpdateProfile}
            disabled={isLoading || isSending || hasPageForSearch}
          />
        </Content>
      </Container>
    </>
  );
}
